import React from "react";
import { useFormik } from "formik";
import swal from "sweetalert";
import Footer from "./common/Footer";
import logo from "../assets/img/logo/newJkLogo.png";
import { submitFeedbackForm } from "./api/feedbackFormApi";

const QUESTION_GROUPS = [
  {
    key: "overallExperience",
    title: "1. Overall Experience",
    question: "How would you rate your overall experience of the event?",
    options: ["Excellent", "Good", "Average", "Poor"],
  },
  {
    key: "eventOrganization",
    title: "2. Event Organization",
    question: "How satisfied were you with the event planning and coordination?",
    options: ["Highly Satisfied", "Satisfied", "Neutral", "Dissatisfied"],
  },
  {
    key: "stayHospitality",
    title: "3. Stay & Hospitality",
    question:
      "How was your experience with accommodation and food arrangements?",
    options: ["Excellent", "Good", "Average", "Poor"],
  },
  {
    key: "plantVisitExperience",
    title: "4. Plant Visit Experience",
    question:
      "How would you rate the plant visit and understanding of operations?",
    options: ["Excellent", "Good", "Average", "Poor"],
  },
  {
    key: "rndVisitExperience",
    title: "5. R&D Visit Experience",
    question: "How informative was the R&D visit and setup demonstration?",
    options: ["Very Informative", "Informative", "Average", "Not Useful"],
  },
  {
    key: "presentationSession",
    title: "6. Presentation Session",
    question:
      "How useful was the presentation in understanding our business and future plans?",
    options: ["Very Useful", "Useful", "Average", "Not Useful"],
  },
];

const TEXT_QUESTIONS = [
  {
    key: "keyTakeaway",
    title: "7. Key Takeaway",
    question: "What was the most valuable part of the event for you?",
  },
  {
    key: "areasOfImprovement",
    title: "8. Areas of Improvement",
    question: "What can we improve for future events?",
  },
  {
    key: "additionalComments",
    title: "9. Additional Comments",
    question: "Any other feedback or suggestions:",
  },
];

export default function FeedbackForm() {
  const formik = useFormik({
    initialValues: {
      mobile: "",
      overallExperience: "",
      eventOrganization: "",
      stayHospitality: "",
      plantVisitExperience: "",
      rndVisitExperience: "",
      presentationSession: "",
      keyTakeaway: "",
      areasOfImprovement: "",
      additionalComments: "",
    },
    validate: (values) => {
      const errors = {};

      if (!values.mobile.trim()) {
        errors.mobile = "Mobile number is required.";
      } else if (!/^\d{10}$/.test(values.mobile)) {
        errors.mobile = "Mobile number must be 10 digits.";
      }

      QUESTION_GROUPS.forEach((question) => {
        if (!values[question.key]) {
          errors[question.key] = "Please select one option.";
        }
      });

      TEXT_QUESTIONS.forEach((question) => {
        if (!values[question.key].trim()) {
          errors[question.key] = "This field is required.";
        }
      });

      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        await submitFeedbackForm(values);
        swal({
          title: "Thank You",
          text: "Thank you for sharing your feedback.",
          icon: "success",
          timer: 2500,
          buttons: false,
        });
        resetForm();
      } catch (error) {
        swal({
          title: "Error",
          text: "Unable to submit feedback right now.",
          icon: "error",
          timer: 2000,
          buttons: false,
        });
      }
    },
  });

  const fieldStyle = {
    width: "100%",
    minHeight: "46px",
    border: "1px solid #d7e1d7",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "#fff",
  };

  const textAreaStyle = {
    ...fieldStyle,
    minHeight: "110px",
    resize: "vertical",
  };

  const errorStyle = {
    color: "#c0392b",
    fontSize: "12px",
    marginTop: "8px",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #f5f7fa 0%, #eef6ee 45%, #f7faf7 100%)",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "920px",
          margin: "0 auto",
          flex: 1,
        }}
      >
        <div
          className="all-single-blog rpt"
          style={{
            backgroundColor: "#fff",
            padding: "20px",
            borderRadius: "16px",
            boxShadow: "0 10px 30px rgba(46, 181, 69, 0.08)",
            border: "1px solid #e3efe4",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <img
              src={logo}
              alt="JK Seeds"
              style={{
                width: "100%",
                maxWidth: "220px",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </div>


          <form onSubmit={formik.handleSubmit}>
            <div
              style={{
                backgroundColor: "#f9fcf9",
                border: "1px solid #e3efe4",
                borderRadius: "16px",
                padding: "20px",
                marginBottom: "20px",
              }}
            >
              <div style={{ marginBottom: "24px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: 700,
                    color: "#295c31",
                  }}
                >
                  Enter Your Mobile Number:
                </label>
                <input
                  type="text"
                  name="mobile"
                  placeholder="Enter 10-digit mobile number"
                  value={formik.values.mobile}
                  onChange={(e) =>
                    formik.setFieldValue(
                      "mobile",
                      e.target.value.replace(/\D/g, "").slice(0, 10)
                    )
                  }
                  onBlur={formik.handleBlur}
                  style={fieldStyle}
                />
                {formik.touched.mobile && formik.errors.mobile ? (
                  <div style={errorStyle}>{formik.errors.mobile}</div>
                ) : null}
              </div>

              {QUESTION_GROUPS.map((item) => (
                <div
                  key={item.key}
                  style={{
                    marginBottom: "24px",
                    paddingBottom: "20px",
                    borderBottom: "1px solid #e6efe6",
                  }}
                >
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#244b2a",
                      marginBottom: "8px",
                    }}
                  >
                    {item.title}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#425d46",
                      marginBottom: "14px",
                      lineHeight: 1.5,
                    }}
                  >
                    {item.question}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "10px",
                    }}
                  >
                    {item.options.map((option) => (
                      <label
                        key={option}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          backgroundColor: "#fff",
                          border: "1px solid #d7e1d7",
                          borderRadius: "12px",
                          padding: "12px 14px",
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#28412c",
                        }}
                      >
                        <input
                          type="radio"
                          name={item.key}
                          value={option}
                          checked={formik.values[item.key] === option}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                  {formik.touched[item.key] && formik.errors[item.key] ? (
                    <div style={errorStyle}>{formik.errors[item.key]}</div>
                  ) : null}
                </div>
              ))}

              {TEXT_QUESTIONS.map((item) => (
                <div key={item.key} style={{ marginBottom: "22px" }}>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: "#244b2a",
                      marginBottom: "8px",
                    }}
                  >
                    {item.title}
                  </div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      color: "#425d46",
                      marginBottom: "10px",
                    }}
                  >
                    {item.question}
                  </label>
                  <textarea
                    name={item.key}
                    value={formik.values[item.key]}
                    onChange={(e) =>
                      formik.setFieldValue(item.key, e.target.value.slice(0, 500))
                    }
                    onBlur={formik.handleBlur}
                    placeholder="Write your answer here"
                    style={textAreaStyle}
                  />
                  {formik.touched[item.key] && formik.errors[item.key] ? (
                    <div style={errorStyle}>{formik.errors[item.key]}</div>
                  ) : null}
                </div>
              ))}
            </div>

            <button
              type="submit"
              style={{
                width: "100%",
                minHeight: "46px",
                borderRadius: "10px",
                border: "none",
                backgroundColor: "#2eb545",
                color: "#fff",
                fontWeight: 700,
                padding: "10px 18px",
                boxShadow: "0 10px 20px rgba(46, 181, 69, 0.2)",
                cursor: "pointer",
                fontSize: "18px",
              }}
            >
              Submit Feedback
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
}
